import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { createTheme, ThemeProvider, PaletteMode } from '@mui/material/styles';
import { getDesignTokens } from '@/theme/GetDesignTokens';

// Extend supported modes
type AppThemeMode = PaletteMode | 'system';

interface ThemeContextValue {
  mode: AppThemeMode;
  setMode: (mode: AppThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextValue>({
  mode: 'light',
  setMode: () => {}
});

export const useThemeMode = () => useContext(ThemeContext);

const THEME_KEY = 'preferred_theme';

export const ThemeContextProvider: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const [mode, setMode] = useState<AppThemeMode>(() => {
    const stored = localStorage.getItem(THEME_KEY);
    return stored === 'dark' || stored === 'light' ? stored : 'system';
  });

  useEffect(() => {
    localStorage.setItem(THEME_KEY, mode);
  }, [mode]);

  const effectiveMode: PaletteMode =
    mode === 'system'
      ? window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light'
      : mode;

  // Apply dark class to document element for CSS-based dark mode
  useEffect(() => {
    const root = document.documentElement;
    if (effectiveMode === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [effectiveMode]);

  const theme = useMemo(
    () => createTheme(getDesignTokens(effectiveMode)),
    [effectiveMode]
  );

  return (
    <ThemeContext.Provider value={{ mode, setMode }}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </ThemeContext.Provider>
  );
};
